/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 📄 File: FaxProgressModal.tsx
 🧩 Description:
    Real-time fax sending progress modal with status polling.
────────────────────────────────────────────────────────────────*/

import { useEffect, useRef, useState } from 'react';
import { CheckCircle, XCircle, Loader2, PhoneCall, FileText, Wifi, Send } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { getFaxJobStatus } from '../api/faxSendingApi';
import type { FaxJobStatus } from '../types';

type Phase = 'connecting' | 'negotiating' | 'transmitting' | 'finishing' | 'done' | 'error';

interface Props {
  isOpen: boolean;
  jobId: string | null;
  callUuid: string | null;
  destinationNumber: string;
  fileName: string;
  onClose: () => void;
}

const PHASE_DURATION_MS = 4000; // simulated phase advancement if no real data yet

// Phase IDs only — labels are resolved via i18n inside the component
const PHASE_IDS: { id: Phase; icon: React.ReactNode; labelKey: string }[] = [
  { id: 'connecting',   icon: <PhoneCall className="h-4 w-4" />,  labelKey: 'progress.connecting' },
  { id: 'negotiating',  icon: <Wifi className="h-4 w-4" />,       labelKey: 'progress.negotiating' },
  { id: 'transmitting', icon: <FileText className="h-4 w-4" />,   labelKey: 'progress.transmitting' },
  { id: 'finishing',    icon: <Send className="h-4 w-4" />,        labelKey: 'progress.finishing' },
];

export default function FaxProgressModal({ isOpen, jobId, callUuid, destinationNumber, fileName, onClose }: Props) {
  const { t } = useI18n();
  const [phase, setPhase] = useState<Phase>('connecting');
  const [result, setResult] = useState<FaxJobStatus | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const phaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const phaseIndexRef = useRef(0);

  // Advance simulated phases while waiting for real result
  const advancePhase = () => {
    const nextIdx = phaseIndexRef.current + 1;
    if (nextIdx < PHASES.length) {
      phaseIndexRef.current = nextIdx;
      setPhase(PHASES[nextIdx].id);
      phaseTimerRef.current = setTimeout(advancePhase, PHASE_DURATION_MS);
    }
  };

  const clearAll = () => {
    if (pollingRef.current)   clearInterval(pollingRef.current);
    if (phaseTimerRef.current) clearTimeout(phaseTimerRef.current);
    if (elapsedRef.current)   clearInterval(elapsedRef.current);
  };

  useEffect(() => {
    if (!isOpen || !jobId) return;

    // Reset state
    setPhase('connecting');
    setResult(null);
    setElapsed(0);
    phaseIndexRef.current = 0;

    // Elapsed timer
    elapsedRef.current = setInterval(() => setElapsed(s => s + 1), 1000);

    // Simulated phase advancement
    phaseTimerRef.current = setTimeout(advancePhase, PHASE_DURATION_MS);

    // Real polling for job status every 4s
    pollingRef.current = setInterval(async () => {
      try {
        const status = await getFaxJobStatus(jobId);
        if (status.status === 'sent') {
          clearAll();
          setPhase('done');
          setResult(status);
        } else if (status.status === 'failed') {
          clearAll();
          setPhase('error');
          setResult(status);
        }
        // If 'queued' → keep polling
      } catch { /* continue polling on error */ }
    }, 4000);

    return clearAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, jobId]);

  if (!isOpen) return null;

  const isDone  = phase === 'done';
  const isError = phase === 'error';
  const isFinished = isDone || isError;

  // Use PHASE_IDS with resolved labels
  const PHASES = PHASE_IDS;

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className={`px-6 py-5 flex items-center gap-3 ${
          isDone  ? 'bg-green-500/10 border-b border-green-500/20' :
          isError ? 'bg-red-500/10 border-b border-red-500/20' :
                    'bg-primary/10 border-b border-primary/20'
        }`}>
          <div className={`rounded-full p-2 ${
            isDone  ? 'bg-green-500/20 text-green-500' :
            isError ? 'bg-red-500/20 text-red-500' :
                      'bg-primary/20 text-primary'
          }`}>
            {isDone  ? <CheckCircle className="h-5 w-5" /> :
             isError ? <XCircle className="h-5 w-5" /> :
                       <Loader2 className="h-5 w-5 animate-spin" />}
          </div>
          <div>
            <h3 className="font-semibold text-foreground">
              {isDone  ? t('faxSending:progress.headerSuccess') :
               isError ? t('faxSending:progress.headerError') :
                         t('faxSending:progress.headerSending')}
            </h3>
            <p className="text-xs text-muted-foreground">
              {t('faxSending:progress.to')}: <span className="font-mono font-medium">{destinationNumber}</span>
              {!isFinished && <span className="ml-2 text-muted-foreground">• {formatTime(elapsed)}</span>}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">

          {/* File info */}
          <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border">
            <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm text-foreground truncate">{fileName}</span>
          </div>

          {/* Progress steps */}
          {!isFinished && (
            <div className="space-y-2">
              {PHASES.map((p, idx) => {
                const currentIdx = PHASES.findIndex(x => x.id === phase);
                const isActive   = idx === currentIdx;
                const isComplete = idx < currentIdx;
                return (
                  <div key={p.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-300 ${
                    isActive   ? 'bg-primary/10 border border-primary/20' :
                    isComplete ? 'opacity-50' :
                                 'opacity-30'
                  }`}>
                    <span className={`${isComplete ? 'text-green-500' : isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                      {isComplete
                        ? <CheckCircle className="h-4 w-4" />
                        : isActive
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : p.icon
                      }
                    </span>
                    <span className={`text-sm ${isActive ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                      {t(`faxSending:${p.labelKey}`)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Result details */}
          {isFinished && result && (
            <div className={`p-4 rounded-lg border ${
              isDone
                ? 'bg-green-500/5 border-green-500/20 text-green-700 dark:text-green-400'
                : 'bg-red-500/5 border-red-500/20 text-red-700 dark:text-red-400'
            }`}>
              {isDone ? (
                <div className="space-y-1 text-sm">
                  {result.pages != null && <div>{t('faxSending:progress.pagesTransmitted')}: <strong>{result.pages}</strong></div>}
                  <div className="text-xs text-muted-foreground">{t('faxSending:progress.totalTime')}: {formatTime(elapsed)}</div>
                </div>
              ) : (
                <div className="text-sm">
                  <div className="font-medium">{t('faxSending:progress.deliveryFailed')}</div>
                  {result.resultText && (
                    <div className="text-xs mt-1 font-mono opacity-70">{result.resultText}</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Still pending hint */}
          {!isFinished && elapsed > 30 && (
            <p className="text-xs text-muted-foreground text-center">
              {t('faxSending:progress.slowHint')}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end">
          {isFinished ? (
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              {t('faxSending:progress.close')}
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-5 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted transition-colors"
            >
              {t('faxSending:progress.minimize')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
